/**
 * RWBR — THE EZRA TROUBLE MATRIX, charts A, B and C (EZRA-S2 §3.5).
 *
 * D-015 chose this over the base game's TROUBLE and over Laurence Davis's
 * Trouble Option sheet, on the grounds that it is deeper. It is: base TROUBLE
 * asks one question ("who is collected?"), and this asks three — what KIND of
 * incident, how OFTEN that kind may happen, and how BADLY it hurts here.
 *
 *   CHART A — the type. A d6 says Accident, Crash or Wreck, distributed to suit
 *             the track, and shifted by the track card's own ESCAPE adjustment.
 *   CHART B — the frequency. Each type gets a number of "spaces" for the race;
 *             a type that has spent all of its spaces cannot be chosen again
 *             until every other type has spent all of theirs.
 *   CHART C — the intensity. Per-type ESCAPE and REPAIR deltas by track type, so
 *             a super-speedway gets more dangerous as the incident escalates
 *             while a short track mellows out.
 *
 * ── THE CHARTS ARE PRINTED. THEY WERE FOUND. ───────────────────────────────
 *
 * `EZRAS2RulesFinal.docx` says "See Appendix C for applicable Charts", and a
 * plain text extraction of that file shows Appendix C as an empty heading. It is
 * not empty: **the charts are an embedded EMF image**, invisible to any text
 * reader. Howard put the file in Drive, the image came out of `word/media/`, and
 * every table below is transcribed from it.
 *
 *   "OPTIONAL TROUBLE MATRIX & TRACK CARD ADJUSTMENTS — Version 4.0 Jan 2017"
 *   Bruce Peckham · Mike Haley
 *
 * ⚠ AN EARLIER VERSION OF THIS FILE CARRIED SEVEN DERIVED ROWS, AND THEY WERE
 * MOSTLY WRONG. Scored against the printed chart, 3 of 9 were right. The two
 * rows labelled `back-solved` — the ones I had the most confidence in — split
 * one right and one wrong. That is the entry worth keeping:
 *
 *   Back-solving from a single worked example recovers the example, not the
 *   rule. My shift rule reproduced Cape Fear and the Road Course perfectly and
 *   was still wrong, because the real modifiers are NAMED PER TRACK TYPE and
 *   apply to the REPAIR adjustment as well as the ESCAPE one. Cape Fear only
 *   fits once its REPAIR [+2] is included — I had matched it with escape alone
 *   by coincidence, on a base matrix that was itself wrong.
 *
 * Nothing in this module is derived any more. Every value below is transcribed.
  */

import type { Dice } from './dice.ts';

export const CITE_MATRIX = 'EZRA-S2 §3.5 — "Optional Trouble Procedure - Accidents, Crashes and Wrecks"';
export const CITE_APPENDIX_C =
  'EZRA-S2 Appendix C — "OPTIONAL TROUBLE MATRIX & TRACK CARD ADJUSTMENTS", Version 4.0 Jan 2017, ' +
  'Bruce Peckham & Mike Haley. Embedded in EZRAS2RulesFinal.docx as an EMF image ' +
  '(word/media/image12.emf), which is why every text extraction of that file shows Appendix C as ' +
  'an empty heading.';

/** EZRA's three kinds of incident. Ordered least to most severe — order matters. */
export const TROUBLE_TYPES = ['AC', 'CR', 'WK'] as const;
export type TroubleType = (typeof TROUBLE_TYPES)[number];

export const TROUBLE_TYPE_NAMES: Readonly<Record<TroubleType, string>> = {
  AC: 'Accident',
  CR: 'Crash',
  WK: 'Wreck',
};

/**
 * EZRA's own characterisation, verbatim. Carried because the booth narrates off
 * it — an Accident and a Wreck should not be described in the same register.
 */
export const TROUBLE_TYPE_CHARACTER: Readonly<Record<TroubleType, readonly string[]>> = {
  AC: [
    'Fewer cars are involved.',
    'Less critical damage.',
    'Threat of lost position is moderate.',
    'Possibility of race exit is lower.',
    'Common to road and short tracks.',
  ],
  CR: [
    'Heavier involvement of cars.',
    'Damage can be critical.',
    'Threat of losing position is significant.',
    'Possibility of race exit increases.',
    'Common to intermediate',
  ],
  WK: [
    'Most serious of all trouble types.',
    'Involvement can be extensive.',
    'Damage can be devastating.',
    'Possibility of race exit is high.',
    'Common to super speedways.',
  ],
};

/** The four categories the printed track cards use. */
export type TrackType = 'Super Speedway' | 'Intermediate Track' | 'Short Track' | 'Road Course';

export type RowSource = 'printed' | 'back-solved' | 'derived';

/** How many of the six d6 faces belong to each type. Always sums to 6. */
export interface Matrix {
  AC: number;
  CR: number;
  WK: number;
}

export interface MatrixRow {
  matrix: Matrix;
  source: RowSource;
  basis: string;
}

// ---------------------------------------------------------------------------
// CHART A — the type
// ---------------------------------------------------------------------------

/**
 * TABLE A — TROUBLE TYPE SELECTION MATRIX, transcribed exactly.
 *
 *   TRACK TYPE   ACCIDENT   CRASH   WRECK
 *   SHORT          1-2       3-5      6
 *   ROAD           1-3       4-5      6
 *   INT'M          1-2       3-4     5-6
 *   SUPER           1        2-3     4-6
 */
export const CHART_A: Readonly<Record<TrackType, MatrixRow>> = {
  'Short Track': { matrix: { AC: 2, CR: 3, WK: 1 }, source: 'printed', basis: 'Appendix C, Table A — SHORT: 1-2 / 3-5 / 6' },
  'Road Course': { matrix: { AC: 3, CR: 2, WK: 1 }, source: 'printed', basis: 'Appendix C, Table A — ROAD: 1-3 / 4-5 / 6' },
  'Intermediate Track': { matrix: { AC: 2, CR: 2, WK: 2 }, source: 'printed', basis: "Appendix C, Table A — INT'M: 1-2 / 3-4 / 5-6" },
  'Super Speedway': { matrix: { AC: 1, CR: 2, WK: 3 }, source: 'printed', basis: 'Appendix C, Table A — SUPER: 1 / 2-3 / 4-6' },
};

/**
 * MATRIX D6 DISTRIBUTION MODIFIERS, transcribed exactly.
 *
 *   TRACK    ESC +1        ESC -1        R +1          R -1
 *   SHORT    1 CR to AC    1 AC to CR    1 CR to AC    1 AC to CR
 *   ROAD     1 CR to AC    1 AC to CR    1 CR to AC    1 AC to CR
 *   INT'M    1 CR to AC    1 CR to WK    1 WK to CR    1 CR to WK
 *   SUPER    1 WK to CR    1 CR to WK    1 WK to CR    1 CR to WK
 *
 * TWO THINGS HERE THAT NO AMOUNT OF REASONING WOULD HAVE PRODUCED.
 *
 * The REPAIR adjustment moves the matrix too. The rules text only ever mentions
 * escape, and every worked example in the prose is an escape example — but the
 * chart has an R+1 and an R-1 column, and Cape Fear's printed matrix cannot be
 * reproduced without them.
 *
 * And the moves are NOT symmetric. On an intermediate track ESC+1 takes a face
 * from CRASH and gives it to ACCIDENT, while ESC-1 takes from CRASH and gives to
 * WRECK — both drain the middle rather than sliding along a ladder. A symmetric
 * ladder is what I assumed, and it is not what they wrote.
 */
export type MatrixMove = readonly [from: TroubleType, to: TroubleType];

export const CHART_A_MODIFIERS: Readonly<
  Record<TrackType, { escapeUp: MatrixMove; escapeDown: MatrixMove; repairUp: MatrixMove; repairDown: MatrixMove }>
> = {
  'Short Track': { escapeUp: ['CR', 'AC'], escapeDown: ['AC', 'CR'], repairUp: ['CR', 'AC'], repairDown: ['AC', 'CR'] },
  'Road Course': { escapeUp: ['CR', 'AC'], escapeDown: ['AC', 'CR'], repairUp: ['CR', 'AC'], repairDown: ['AC', 'CR'] },
  'Intermediate Track': { escapeUp: ['CR', 'AC'], escapeDown: ['CR', 'WK'], repairUp: ['WK', 'CR'], repairDown: ['CR', 'WK'] },
  'Super Speedway': { escapeUp: ['WK', 'CR'], escapeDown: ['CR', 'WK'], repairUp: ['WK', 'CR'], repairDown: ['CR', 'WK'] },
};

/**
 * Apply a track card's ESCAPE and REPAIR adjustments to the base matrix.
 *
 * EZRA: "There is one limitation: modification of a Chart A Matrix must leave at
 * least one of each Trouble type within the Matrix." That floor is not a tidy
 * guard, it is load-bearing — Cape Fear's REPAIR [+2] would empty the WRECK
 * column outright, and the printed matrix on the card shows it clamped at one.
 */
export function shiftMatrix(track: TrackType, base: Matrix, escapeAdjustment = 0, repairAdjustment = 0): Matrix {
  const mods = CHART_A_MODIFIERS[track];
  if (!mods) throw new RangeError(`no Chart A modifiers for track type "${track}"`);
  const m = { ...base };
  const move = ([from, to]: MatrixMove, times: number): void => {
    for (let i = 0; i < times; i++) {
      if (m[from] <= 1) return; // the one-of-each floor
      m[from]--;
      m[to]++;
    }
  };
  if (escapeAdjustment > 0) move(mods.escapeUp, escapeAdjustment);
  else if (escapeAdjustment < 0) move(mods.escapeDown, -escapeAdjustment);
  if (repairAdjustment > 0) move(mods.repairUp, repairAdjustment);
  else if (repairAdjustment < 0) move(mods.repairDown, -repairAdjustment);
  return m;
}

/** The matrix actually in force for a race. */
export function matrixFor(
  track: TrackType,
  escapeAdjustment = 0,
  repairAdjustment = 0,
): MatrixRow & { effective: Matrix } {
  const row = CHART_A[track];
  if (!row) throw new RangeError(`no Chart A row for track type "${track}"`);
  return { ...row, effective: shiftMatrix(track, row.matrix, escapeAdjustment, repairAdjustment) };
}

/** Which type a given d6 face lands on, reading AC then CR then WK. */
export function typeForFace(m: Matrix, face: number): TroubleType {
  if (face < 1 || face > 6) throw new RangeError(`a d6 face must be 1-6, got ${face}`);
  if (face <= m.AC) return 'AC';
  if (face <= m.AC + m.CR) return 'CR';
  return 'WK';
}

/** `AC 1  CR 2-4  WK 5-6` — how the matrix prints on a track card. */
export function describeMatrix(m: Matrix): string {
  const span = (lo: number, n: number) => (n === 1 ? `${lo}` : `${lo}-${lo + n - 1}`);
  return `AC ${span(1, m.AC)}  CR ${span(1 + m.AC, m.CR)}  WK ${span(1 + m.AC + m.CR, m.WK)}`;
}

// ---------------------------------------------------------------------------
// CHART B — the frequency
// ---------------------------------------------------------------------------

/**
 * TABLE B — TROUBLE EVENT DISTRIBUTION BY TROUBLE AND TRACK TYPES, transcribed
 * by counting the printed ( ) spaces.
 *
 *                30  40  50  60   |               30  40  50  60
 *   SHORT   AC    1   2   3   4   |   INT'M   AC   1   1   1   1
 *           CR    1   1   1   1   |           CR   1   2   3   2
 *           WK    1   1   1   1   |           WK   1   1   1   3
 *   ROAD    AC    1   2   2   2   |   SUPER   AC   1   1   1   1
 *           CR    1   1   2   3   |           CR   1   1   1   1
 *           WK    1   1   1   1   |           WK   1   2   3   4
 *
 * Every column sums to the printed rule — three spaces at 30 turns, one more per
 * ten turns — which is the check that the counting is right.
 *
 * EZRA's own rounding: "For races with length <30 turns use '30'. Races with odd
 * lengths use the 10's digit (48 turns = '40'). Races >60 turns. Why are you
 * punishing yourself?"
 *
 * ⚠ ONE PRINTED ODDITY, REPRODUCED RATHER THAN TIDIED. The INT'M row runs CRASH
 * 1, 2, 3, **2** across 30/40/50/60 while WRECK jumps 1, 1, 1, **3**. Every other
 * row in the table is monotonic. Read literally, a 60-turn intermediate race
 * turns into a wreck track — defensible as a statement about long races on a
 * mile-and-a-half, and not mine to smooth out.
 */
export const CHART_B: Readonly<
  Record<TrackType, Readonly<Record<30 | 40 | 50 | 60, Readonly<Record<TroubleType, number>>>>>
> = {
  'Short Track': {
    30: { AC: 1, CR: 1, WK: 1 },
    40: { AC: 2, CR: 1, WK: 1 },
    50: { AC: 3, CR: 1, WK: 1 },
    60: { AC: 4, CR: 1, WK: 1 },
  },
  'Road Course': {
    30: { AC: 1, CR: 1, WK: 1 },
    40: { AC: 2, CR: 1, WK: 1 },
    50: { AC: 2, CR: 2, WK: 1 },
    60: { AC: 2, CR: 3, WK: 1 },
  },
  'Intermediate Track': {
    30: { AC: 1, CR: 1, WK: 1 },
    40: { AC: 1, CR: 2, WK: 1 },
    50: { AC: 1, CR: 3, WK: 1 },
    60: { AC: 1, CR: 2, WK: 3 },
  },
  'Super Speedway': {
    30: { AC: 1, CR: 1, WK: 1 },
    40: { AC: 1, CR: 1, WK: 2 },
    50: { AC: 1, CR: 1, WK: 3 },
    60: { AC: 1, CR: 1, WK: 4 },
  },
};

export const CHART_B_SOURCE: RowSource = 'printed';
export const CHART_B_BASIS = 'Appendix C, Table B — counted from the printed ( ) spaces.';

export function chartBTurnBand(turns: number): 30 | 40 | 50 | 60 {
  if (turns < 30) return 30;
  return Math.min(60, Math.floor(turns / 10) * 10) as 30 | 40 | 50 | 60;
}

export function spacesFor(track: TrackType, turns: number): Record<TroubleType, number> {
  const row = CHART_B[track];
  if (!row) throw new RangeError(`no Chart B row for track type "${track}"`);
  return { ...row[chartBTurnBand(turns)] };
}


/**
 * The index card. EZRA: "as a specific trouble event occurs, mark off the
 * corresponding space… Once the space(s) allocated to a trouble type are
 * completely marked off, you can't choose from that trouble category again from
 * the Chart A results until every other space is completely marked off. Once
 * every space is completely marked off, start over from the beginning."
 *
 * THIS IS WHAT MAKES THE FEATURE WORTH HAVING. Without it, Chart A alone would
 * cheerfully produce six wrecks in a row at Talladega — the matrix has no
 * memory. The tracker is the memory, and it is the difference between "the dice
 * were hot" and a race that tells a story.
 */
export class ChartBTracker {
  private readonly allocated: Record<TroubleType, number>;
  private used: Record<TroubleType, number>;
  /** Every reset, for the audit trail. */
  public resets = 0;

  constructor(track: TrackType, turns: number) {
    this.allocated = spacesFor(track, turns);
    this.used = { AC: 0, CR: 0, WK: 0 };
  }

  spaces(): Readonly<Record<TroubleType, number>> {
    return { ...this.allocated };
  }

  remaining(type: TroubleType): number {
    return this.allocated[type] - this.used[type];
  }

  available(): TroubleType[] {
    return TROUBLE_TYPES.filter((t) => this.remaining(t) > 0);
  }

  /**
   * Take a space for `wanted`, or — when its spaces are spent — one of the
   * others, chosen as EZRA says: **"Randomly select from the remaining spaces."**
   *
   * I ORIGINALLY SUBSTITUTED THE NEAREST TYPE BY SEVERITY, on the reasoning that
   * it disturbs the track's character least. That is a nicer idea than the one
   * they printed, and it is not the one they printed. Drawing proportionally to
   * spaces REMAINING is what "randomly select from the remaining spaces" says,
   * and it matters: the nearest-severity rule made substitution deterministic,
   * so a super-speedway that ran out of Wrecks produced an unbroken march of
   * Crashes instead of a mix.
   *
   * `dice` is optional so the tracker stays usable from a test without one; with
   * no dice it falls back to the most-remaining type, which is deterministic and
   * declared rather than accidental.
   */
  take(wanted: TroubleType, dice?: Dice): { type: TroubleType; substituted: boolean; reset: boolean } {
    let reset = false;
    if (this.available().length === 0) {
      this.used = { AC: 0, CR: 0, WK: 0 };
      this.resets++;
      reset = true;
    }
    if (this.remaining(wanted) > 0) {
      this.used[wanted]++;
      return { type: wanted, substituted: false, reset };
    }
    const open = this.available();
    let chosen: TroubleType;
    if (dice) {
      // Weighted by spaces remaining, which is what "the remaining spaces" are.
      const pool: TroubleType[] = [];
      for (const t of open) for (let i = 0; i < this.remaining(t); i++) pool.push(t);
      chosen = pool[dice.about(undefined, () => dice.select(pool.length), 'trouble-space') - 1];
    } else {
      chosen = [...open].sort((a, b) => this.remaining(b) - this.remaining(a))[0];
    }
    this.used[chosen]++;
    return { type: chosen, substituted: true, reset };
  }
}

// ---------------------------------------------------------------------------
// CHART C — the intensity
// ---------------------------------------------------------------------------

export interface Intensity {
  escape: number;
  repair: number;
}

/**
 * TABLE C — ADDITIONAL ADJUSTMENTS TO TRACK CARD TROUBLE AND REPAIR RANGES,
 * transcribed exactly. Applied ON TOP of the track card's own adjustments.
 *
 *   TRACK      ACCIDENT       CRASH          WRECK
 *              ESC   RPR      ESC   RPR      ESC   RPR
 *   SHORT      NC    +1       NC    NC       NC    NC
 *   ROAD       +1    +1       NC    +1       NC    NC
 *   INT'M      NC    NC       NC    NC       NC    NC
 *   SUPER      NC    NC       -1    NC       -1    -1
 *
 * The intermediate row is NC in all six cells. A whole track category for which
 * Chart C does nothing is not something anyone would invent, and it is what they
 * printed: the intermediate is the neutral reference the other three bend away
 * from — short and road tracks get gentler at the soft end, the super speedway
 * gets harsher at the hard end, and the middle of the sport is the baseline.
 *
 * EZRA: "The Optional Trouble Adjustments are restricted to Group Trouble events.
 * They do not apply to individual driver trouble (Feuds, Driver Incidents, etc.)"
 */
export const CHART_C: Readonly<Record<TrackType, Readonly<Record<TroubleType, Intensity>>>> = {
  'Short Track': {
    AC: { escape: 0, repair: 1 },
    CR: { escape: 0, repair: 0 },
    WK: { escape: 0, repair: 0 },
  },
  'Road Course': {
    AC: { escape: 1, repair: 1 },
    CR: { escape: 0, repair: 1 },
    WK: { escape: 0, repair: 0 },
  },
  'Intermediate Track': {
    AC: { escape: 0, repair: 0 },
    CR: { escape: 0, repair: 0 },
    WK: { escape: 0, repair: 0 },
  },
  'Super Speedway': {
    AC: { escape: 0, repair: 0 },
    CR: { escape: -1, repair: 0 },
    WK: { escape: -1, repair: -1 },
  },
};

export const CHART_C_SOURCES: Readonly<Record<TrackType, RowSource>> = {
  'Super Speedway': 'printed',
  'Intermediate Track': 'printed',
  'Short Track': 'printed',
  'Road Course': 'printed',
};

export function intensityFor(track: TrackType, type: TroubleType): Intensity {
  const row = CHART_C[track];
  if (!row) throw new RangeError(`no Chart C row for track type "${track}"`);
  return row[type];
}

// ---------------------------------------------------------------------------
// INVOLVEMENT — printed in full, twice
// ---------------------------------------------------------------------------

/**
 * "Involvement refers to the number of cars to be checked when a trouble
 * incident occurs."
 *
 *          GROUP      AC          CR              WK
 *          TOP        2D6/3       2D6/2           GROUP
 *          MID        2D6/2       2D6             GROUP
 *          BOT        2D6         2D6 & 2D6/3     GROUP
 *
 * The rules text prints the BOT/CR cell as "2D6+ & 2D6+3", but the same
 * document's own worked example defines the notation — "2D6&2D6/3 tells you to
 * roll 2d6 added then roll 2d6 added and divided by 3 and add both products
 * together" — and the Cape Fear track card prints it as "2D6 & 2D6/3". The "+3"
 * is a typo for "/3", confirmed by two independent places in one document.
 *
 * GROUP means every car in the group is checked, which is what the base game
 * already does — so a Wreck IS base-game TROUBLE, and Accidents and Crashes are
 * the new, smaller things.
 */
export type IncidentGroup = 'top' | 'middle' | 'bottom';

export function involvement(
  group: IncidentGroup,
  type: TroubleType,
  dice: Dice,
): { cars: number | 'group'; rolls: number[]; formula: string } {
  if (type === 'WK') return { cars: 'group', rolls: [], formula: 'GROUP' };

  const two = () => dice.sum2d6().total;
  if (group === 'top') {
    const r = two();
    return type === 'AC'
      ? { cars: Math.floor(r / 3), rolls: [r], formula: '2D6/3' }
      : { cars: Math.floor(r / 2), rolls: [r], formula: '2D6/2' };
  }
  if (group === 'middle') {
    const r = two();
    return type === 'AC'
      ? { cars: Math.floor(r / 2), rolls: [r], formula: '2D6/2' }
      : { cars: r, rolls: [r], formula: '2D6' };
  }
  if (type === 'AC') {
    const r = two();
    return { cars: r, rolls: [r], formula: '2D6' };
  }
  const a = two();
  const b = two();
  return { cars: a + Math.floor(b / 3), rolls: [a, b], formula: '2D6 & 2D6/3' };
}

// ---------------------------------------------------------------------------
// THE BIG ONE — printed in full
// ---------------------------------------------------------------------------

/**
 * "The number of 'collected' drivers that trigger The Big One are: Top Group
 * (3); Middle Group (6) and Bottom Group (12). There is no Big One with an
 * Accident Event. The Big One in the Bottom Group wraps to the Top Group on a
 * 1d6 roll of 6."
 *
 * Note this REPLACES the base game's thresholds, which `trouble.ts` carries as
 * `BIG_ONE_THRESHOLD` (top 3, middle 6, bottom null). Top and middle agree; the
 * bottom group is the difference — base RWBR has no cascade below MIDDLE at all,
 * and EZRA gives it one that wraps back to the front of the field.
 */
export const EZRA_BIG_ONE_THRESHOLD: Readonly<Record<IncidentGroup, number>> = {
  top: 3,
  middle: 6,
  bottom: 12,
};

export const BOTTOM_WRAP_ON = 6;

export function bigOneFrom(
  group: IncidentGroup,
  type: TroubleType,
  collected: number,
  dice: Dice,
): { triggers: boolean; into: IncidentGroup | null; wrapRoll: number | null; reason: string } {
  if (type === 'AC') {
    return { triggers: false, into: null, wrapRoll: null, reason: 'there is no Big One with an Accident Event' };
  }
  if (collected < EZRA_BIG_ONE_THRESHOLD[group]) {
    return {
      triggers: false,
      into: null,
      wrapRoll: null,
      reason: `${collected} collected, under the ${group} threshold of ${EZRA_BIG_ONE_THRESHOLD[group]}`,
    };
  }
  if (group === 'top') return { triggers: true, into: 'middle', wrapRoll: null, reason: 'the Big One cascades down' };
  if (group === 'middle') return { triggers: true, into: 'bottom', wrapRoll: null, reason: 'the Big One cascades down' };

  // The bottom group has nothing below it, so EZRA sends it back to the front.
  const roll = dice.d6();
  return roll === BOTTOM_WRAP_ON
    ? { triggers: true, into: 'top', wrapRoll: roll, reason: `a ${roll} wraps the Big One up to the TOP group` }
    : { triggers: false, into: null, wrapRoll: roll, reason: `a ${roll} — the wrap needs a ${BOTTOM_WRAP_ON}` };
}

// ---------------------------------------------------------------------------
// The audit
// ---------------------------------------------------------------------------

export interface SourceNote {
  chart: 'A' | 'B' | 'C';
  track: TrackType | 'all';
  source: RowSource;
  basis: string;
}

/**
 * Every row in this module that is not printed, and why it says what it says.
 *
 * IT NOW RETURNS EMPTY, and the function stays anyway. It was written when seven
 * of these nine rows were guesses, and it is what made replacing them a
 * mechanical job rather than an archaeology one. A derived chart nobody
 * remembers is derived is indistinguishable from a transcribed one; this is what
 * keeps them distinguishable, including the next time something is missing.
 */
export function auditMatrixSources(): SourceNote[] {
  const notes: SourceNote[] = [];
  for (const track of Object.keys(CHART_A) as TrackType[]) {
    const row = CHART_A[track];
    if (row.source !== 'printed') notes.push({ chart: 'A', track, source: row.source, basis: row.basis });
  }
  if (CHART_B_SOURCE !== 'printed') notes.push({ chart: 'B', track: 'all', source: CHART_B_SOURCE, basis: CHART_B_BASIS });
  for (const track of Object.keys(CHART_C_SOURCES) as TrackType[]) {
    const source = CHART_C_SOURCES[track];
    if (source !== 'printed') {
      notes.push({
        chart: 'C',
        track,
        source,
        basis:
          source === 'back-solved'
            ? 'Recovered from the rule text\'s Daytona worked example, and independently confirmed by the Cape Fear track card printing the same deltas.'
            : 'NOT PRINTED. Follows EZRA\'s one sentence: super speedways get more dangerous as the type escalates; short tracks and road courses mellow out at the less intense types.',
      });
    }
  }
  return notes;
}

/** Map a track card's printed category onto a Chart A/B/C row. */
export function trackTypeOf(category: string): TrackType {
  const c = category.trim().toLowerCase();
  if (c.includes('super')) return 'Super Speedway';
  if (c.includes('road')) return 'Road Course';
  if (c.includes('short')) return 'Short Track';
  return 'Intermediate Track';
}
